import { useCallback, useState } from 'react'
import { Contract, ethers } from 'ethers'
import { useTranslation } from 'react-i18next'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useWeb3ProviderAndContract } from 'hooks/useWeb3ProviderAndContract'
import { useSwitchChainForContractTx } from 'hooks/useSwitchChainForContractTx'
import useToast from 'hooks/useToast'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import { ERC20_ABI } from 'utils/swap/abi'
import { adapterFor } from 'utils/swap/execute'
import { decodeSwapError, QuoteStaleError, retryOnEmptySimulation } from 'utils/swap/errorMessages'
import { deadlineFromNow, minAmountOut } from 'utils/swap/rank'
import { nameRouterError, withGasMargin, wrapForRouter } from 'utils/swap/router'
import { reportClientError } from 'utils/reportClientError'
import type { Quote, SwapSettings, SwapToken } from 'utils/swap/types'

export type SwapStep = 'idle' | 'approving' | 'swapping'

/** Base confirms in seconds; two minutes without a receipt is worth a record. */
const PENDING_TOO_LONG_MS = 120_000

export interface UseSwapExecutionResult {
  step: SwapStep
  /** The hash of whatever is in flight, so the user has their own record. */
  txHash: string | null
  /** Whether the router still needs an approval for this amount. */
  needsApproval: boolean
  refreshAllowance: () => Promise<void>
  approve: () => Promise<boolean>
  swap: () => Promise<boolean>
}

async function readBalance(provider: ethers.providers.Provider, token: SwapToken, account: string): Promise<bigint> {
  if (token.isNative) return (await provider.getBalance(account)).toBigInt()
  const erc20 = new Contract(token.address, ERC20_ABI as unknown as ethers.ContractInterface, provider)
  const balance: ethers.BigNumber = await erc20.balanceOf(account)
  return balance.toBigInt()
}

/**
 * Turns a chosen quote into a transaction, in five steps and in this order.
 *
 * The one that is easy to leave out is the third. Every send here is dry-run
 * first with the exact arguments it will carry, so a route that would revert
 * is refused before a wallet prompt ever appears — the user reads a sentence
 * instead of approving something and paying for it to fail. It also catches the
 * case a quote cannot: the price having moved past the floor between the quote
 * and the press.
 */
export function useSwapExecution(params: {
  chainId: number
  quote: Quote | null
  tokenIn: SwapToken | null
  tokenOut: SwapToken | null
  settings: SwapSettings
  burnBps: number
  /** The platform router (scope 16). When set, every swap is wrapped into it and it is the only spender. */
  routerAddress?: string | null
  onDone?: () => void
}): UseSwapExecutionResult {
  const { chainId, quote, tokenIn, tokenOut, settings, burnBps, routerAddress = null, onDone } = params
  const { t } = useTranslation()
  const { account } = useWeb3React()
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()
  const ensureChainForTx = useSwitchChainForContractTx()
  const { toastSuccess, toastError } = useToast()

  const [step, setStep] = useState<SwapStep>('idle')
  const [txHash, setTxHash] = useState<string | null>(null)
  const [allowance, setAllowance] = useState<bigint | null>(null)

  /*
   * Who the approval names. Through the platform router it is always the
   * router — one spender per token instead of one per venue — and the venue
   * is approved by the router itself, for exactly the net amount, inside the
   * transaction.
   */
  const spender =
    quote && tokenIn && tokenOut
      ? routerAddress && !tokenIn.isNative
        ? routerAddress
        : adapterFor(quote.candidate.venue).spender({
            signer: {} as ethers.Signer,
            account: account ?? ethers.constants.AddressZero,
            quote,
            tokenIn,
            tokenOut,
            minOut: 0n,
            deadline: 0n,
            slippageBps: settings.slippageBps,
            routerAddress
          })
      : null

  const refreshAllowance = useCallback(async () => {
    if (!account || !tokenIn || tokenIn.isNative || !spender) {
      setAllowance(null)
      return
    }
    try {
      const provider = getSimpleRpcProviderByChainId(chainId) as ethers.providers.Provider
      const erc20 = new Contract(tokenIn.address, ERC20_ABI as unknown as ethers.ContractInterface, provider)
      const current: ethers.BigNumber = await erc20.allowance(account, spender)
      setAllowance(current.toBigInt())
    } catch {
      // An allowance we cannot read is treated as absent: the worst outcome is
      // an approval the user did not strictly need, and the dry run catches
      // the opposite mistake before it costs anything.
      setAllowance(null)
    }
  }, [account, chainId, spender, tokenIn])

  // The approval covers what leaves the wallet: the gross amount when the router takes its fee.
  const approvalAmount = quote ? quote.grossAmountIn ?? quote.amountIn : 0n
  const needsApproval = Boolean(
    quote && tokenIn && !tokenIn.isNative && spender && (allowance === null || allowance < approvalAmount)
  )

  const approve = useCallback(async (): Promise<boolean> => {
    if (!account || !quote || !tokenIn || !spender) return false
    if (!(await ensureChainForTx(chainId))) return false
    setStep('approving')
    try {
      const signer = getProviderOrSignerByChainId(chainId, account)
      const erc20 = new Contract(
        tokenIn.address,
        ERC20_ABI as unknown as ethers.ContractInterface,
        signer as unknown as ethers.Signer
      )
      /*
       * The exact amount, to this router, and never MaxUint256. An unlimited
       * approval outlives the trade it was made for, and the address it is
       * made to is a third party's contract.
       */
      const tx = await erc20.approve(spender, ethers.BigNumber.from(approvalAmount.toString()))
      setTxHash(tx.hash)
      await tx.wait()
      await refreshAllowance()
      toastSuccess(t('swap.approveOk', { token: tokenIn.symbol }))
      return true
    } catch (err) {
      toastError(
        decodeSwapError(err, t, {
          venue: quote.candidate.venue,
          isMproIn: tokenIn.isMpro,
          burnBps
        })
      )
      return false
    } finally {
      setStep('idle')
      setTxHash(null)
    }
  }, [
    account,
    approvalAmount,
    burnBps,
    chainId,
    ensureChainForTx,
    getProviderOrSignerByChainId,
    quote,
    refreshAllowance,
    spender,
    t,
    toastError,
    toastSuccess,
    tokenIn
  ])

  const swap = useCallback(async (): Promise<boolean> => {
    if (!account || !quote || !tokenIn || !tokenOut) return false
    if (!(await ensureChainForTx(chainId))) return false

    const context = {
      venue: quote.candidate.venue,
      isMproIn: tokenIn.isMpro,
      burnBps
    }
    setStep('swapping')
    try {
      const signer = getProviderOrSignerByChainId(chainId, account) as unknown as ethers.Signer
      const readProvider = getSimpleRpcProviderByChainId(chainId) as ethers.providers.Provider
      const minOut = minAmountOut(quote.amountOut, settings.slippageBps)
      const adapter = adapterFor(quote.candidate.venue)
      const executeArgs = {
        signer,
        account,
        quote,
        tokenIn,
        tokenOut,
        minOut,
        // Recomputed here rather than carried from the quote: a deadline
        // measured when the price was read is already part-spent by the time
        // anybody presses a button.
        deadline: deadlineFromNow(settings.deadlineMinutes),
        slippageBps: settings.slippageBps,
        routerAddress
      }
      const venueTx = await adapter.buildTx(executeArgs)
      /*
       * Through the platform router (scope 16): the venue transaction the
       * adapter built — for the net amount, recipient = this wallet — becomes
       * the payload of one router.swap(); the router takes the fee and pays
       * the venue. The dry run below runs the whole thing.
       */
      const tx = routerAddress ? wrapForRouter(venueTx, executeArgs, routerAddress) : venueTx

      /*
       * The dry run. Same arguments, same sender, no signature and no gas — so
       * a route that cannot be filled is refused here, with a sentence, rather
       * than in the user's wallet after they have paid for it.
       */
      await retryOnEmptySimulation(() => signer.call({ ...tx, from: account }))
      if (routerAddress) {
        // A node's estimate for router → venue → pools has come in low on the
        // fork rehearsal; a margin costs nothing that is not refunded.
        tx.gasLimit = withGasMargin(
          await retryOnEmptySimulation(() => signer.estimateGas({ ...tx, from: account }))
        )
      }

      /*
       * What actually arrived, read from the wallet rather than from the
       * quote. A fee-on-transfer token delivers less than the router reports,
       * and a success message that repeats the quote would be telling the user
       * a number they did not receive.
       */
      const before = await readBalance(readProvider, tokenOut, account)

      const sent = await signer.sendTransaction(tx)
      setTxHash(sent.hash)
      /*
       * A transaction that never confirms is the one failure nothing else
       * records: the chain scan only sees swaps that landed, and the catch
       * below only sees a throw. So a wait that passes two minutes is
       * reported once — hash, venue, router — while the wait goes on.
       */
      const slow = setTimeout(() => {
        reportClientError({
          message: `swap.pendingTooLong: ${sent.hash} venue=${quote.candidate.venue} router=${routerAddress ?? 'none'}`,
          kind: 'transaction',
          handled: true
        })
      }, PENDING_TOO_LONG_MS)
      try {
        await sent.wait()
      } finally {
        clearTimeout(slow)
      }

      const after = await readBalance(readProvider, tokenOut, account)
      const received = after > before ? after - before : 0n
      toastSuccess(
        t('swap.swapOk', {
          amount: ethers.utils.formatUnits(received.toString(), tokenOut.decimals),
          token: tokenOut.symbol
        })
      )
      await refreshAllowance()
      onDone?.()
      return true
    } catch (err) {
      // The router's own refusals arrive as custom errors; give the decoder their name.
      const routerError = nameRouterError(err)
      toastError(decodeSwapError(routerError ? new Error(`router:${routerError}`) : err, t, context))
      return false
    } finally {
      setStep('idle')
    }
  }, [
    account,
    burnBps,
    chainId,
    ensureChainForTx,
    getProviderOrSignerByChainId,
    onDone,
    quote,
    refreshAllowance,
    routerAddress,
    settings.deadlineMinutes,
    settings.slippageBps,
    t,
    toastError,
    toastSuccess,
    tokenIn,
    tokenOut
  ])

  return { step, txHash, needsApproval, refreshAllowance, approve, swap }
}

export { QuoteStaleError }
